// Customer BOAT SALE AGREEMENT (BOS) export — faithfully reproduces the official TECHNOHULL BOS
// template (header logo, buyer/delivery/vessel, general characteristics, vessel configuration,
// standard equipment, engines & optional pricing, totals, disclaimer, payment schedule, bank
// details, the full Terms of Sale appendix, and the stamp + signature block). Rendered into a
// print window (print / save as PDF). Two entry points build the same normalized doc:
//   • exportSalesAgreement(...)          — from the live Configurator (catalogue + selection)
//   • exportAgreementFromSnapshot(...)   — from a committed Sales Order snapshot

import { DEALER_DISCOUNT_PCT, VAT_PCT, type OrderSnapshot } from '@technohull/shared';
import type { CatalogueCategory } from './orders.js';
import { LOGO_DATA_URI, WORDMARK_DATA_URI, SEAL_SIGNATURE_DATA_URI } from './agreementAssets.js';
import { formatClientDate } from '../lib/dates.js';

// ── normalized document the renderer consumes ────────────────────────────────────
interface OptionalLine { name: string; qty: number; unit: number; amount: number; discountEligible: boolean }
interface AgreementDoc {
  title: string;
  proformaNumber: string;
  orderDate: string;
  buyer: { name: string; registeredAddress: string; vatTaxCode: string; profession: string; email: string; telephone: string };
  delivery: { method: string; destination: string; estimatedDeliveryPeriod: string };
  vessel: { modelName: string; modelYear: string; flag: string; vesselName: string };
  hull: string; // SAP / hull tracking number (the proforma's "T#")
  modelKey: string;
  configChoices: Array<[string, string]>;
  standardGroups: Array<{ title: string; items: string[] }>;
  optionalLines: OptionalLine[];
  pricing: { optional: number; discount: number; net: number; vat: number; total: number };
}

const esc = (s: unknown) => String(s ?? '').replace(/[&<>]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;' }[c] as string));
const eur = (n: number) => `€ ${(n || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const modelKeyOf = (name: unknown) => String(name ?? '').toUpperCase().replace(/[^A-Z0-9]/g, '');

// ── static content from the official template ─────────────────────────────────────
const CHARACTERISTICS: Record<string, Array<[string, string]>> = {
  ALPHA40: [
    ['LOA', '12.1 m'], ['BEAM', '3.6 m'], ['LIGHT SHIP WEIGHT (excluding the engines)', 'Approx. 4,900 kg'],
    ['FUEL CAPACITY', '800 L'], ['WATER CAPACITY', '130 L'], ['ENGINE OPTIONS', 'Outboard / Inboard'],
    ['MAX POWER', '2 x 500 HP'], ['NUMBER OF PASSENGERS', '12'],
    ['HULL DESIGN', 'Dynastream Deep V with ventilated steps'], ['CLASS CERTIFICATION', 'B-Offshore'],
  ],
};

const CONFIG_CATEGORIES = ['AFT CONFIGURATION', 'HULL AND DECK COLOR', 'TUBES MATERIAL', 'UPHOLSTERY'];

const DISCLAIMER = [
  'The specifications of the boat are not contractual. Specification may be modified without prior notice. Price not including VAT or taxes, shipping & handling, insurance, handover and launching. Performance may vary due to equipment change, weather and load conditions.',
  'The yard will try in good faith (but will not be under obligation) to meet special requests.',
  'Prices are subject to change until initial deposit is received.',
  'Shipping and transport costs are excluded from any discount.',
  'Offer is valid for 5 days.',
];

const FOOTNOTES: Array<[string, string]> = [
  ['*', 'The vessel is sold and delivered on an ex-works basis i.e. all cost and risk of transport, export / import, custom clearance fees and insurance cost related to the sale shall be borne by the Customer. Delivery shall be made at such of the Company’s premises in Greece indicated by the Company in its sole discretion. The Company assumes no liability whatsoever after such point even if it agrees to help the Customer transfer the vessel to the forwarding company’s premises (as such is chosen by the Customer) or load the vessel for transport.'],
  ['**', 'Delivery days are calculated on valid Sale Agreements (par. 4(a) of the Terms of Sale) and given in good faith only. Accordingly, by reason of handmade production of the vessel, the actual delivery date may differ and the Customer may not terminate this sale agreement on grounds related to the actual delivery date. Final payment must be received at least a week prior to the delivery date, failure to do so may delay shipping. Non timely installment payments may result in significant delays in production process. The Company shall not be under obligation to continue production in the event of missing installment payments.'],
  ['***', 'Initial payment on signing of this sale agreement and shall constitute a condition precedent for the effect hereof. In the absence of signature on the part of the Customer on this Sale Agreement, payment of the initial payment shall be deemed acceptance of this Sale Agreement and all terms hereof including the Terms of Sale appended hereto. The initial payment is non-refundable.'],
];

const FUNDS_NOTE = 'Funds must always be deposited from the same person/company that will be invoiced. Prior to signing Customers must take their time and read carefully the vessel specifications, equipment and associated deliverables. Once this vessel Sale Agreement is signed and/or once the initial payment is paid, none of the above can be amended or otherwise changed. By signing this vessel Sale Agreement, the Customer declares that he has also read, understood and accepts the Terms of Sale Appended hereto.';

const BANKS: Array<{ name: string; iban: string; bic: string }> = [
  { name: 'ALPHA BANK', iban: 'GR47 0140 1350 1350 0200 2016 203', bic: 'CRBAGRAAXXX' },
  { name: 'EUROBANK', iban: 'GR77 0260 2200 0009 9020 0435 169', bic: 'ERBKGRAA' },
];

const TERMS_INTRO = 'In the absence of express written agreement to the contrary all sale contacts with Technohull SA are subject to the following terms of sale and supply.';
const TERMS: Array<{ h: string; p: string[] }> = [
  { h: '1. INTERPRETATION', p: [
    '“Company” means TECHNOHULL SA its successors and/or its assigns.',
    '“Bill of Sale” means the standard bill of sale of the Company, whereby title to the Vessel is transferred to the Customer.',
    '“Sale Agreement” means the sales agreement for goods, duly signed by the Company and the Customer, and valid.',
    '“Customer” means the business entity or person named in the Sale Agreement.',
    '“Goods” or “Vessel” means the vessel described in the Sale Agreement.',
  ] },
  { h: '2. PRICE', p: [
    'The Company reserves the right at any time to change the price stated in the Sale Agreement in the event of any extraordinary increase in the actual cost of operation, labour or materials.',
  ] },
  { h: '3. SPECIFICATIONS', p: [
    'a) The Company will supply the Vessel described and specified in the Sale Agreement.',
    'b) In the absence of optional specifications in the Sale Agreement the Company will supply the Vessel described in the Sale Agreement, as set out in the Company standard specifications for those goods on the date of the Sales Agreement.',
    'c) The Company reserves the right to make non-material or non-detrimental changes to the specification from time to time without notice to the Customer, in the event of technical upgrades or changes of material or equipment that during the period of construction has become obsolete or discontinued.',
  ] },
  { h: '4. SALE AGREEMENT, SALE & DELIVERY', p: [
    'a) A Sale Agreement is valid, when the initial payment indicated in clause 5(a) herein has been paid by the Customer and received by the Company. By making such initial payment the Customer acknowledges that it has read and accepted the Sale Agreement and these Terms of Sale.',
    'b) Delivery days are calculated on valid Sale Agreements and given in good faith.',
    'c) The Company shall not be liable for any consequences of delivery on a day later than the one calculated in the Sale Agreement, unless such late delivery is caused by its wilful misconduct.',
    'd) All vessels are sold and delivered on an ex-works basis i.e. all cost and risk of transport, export / import and insurance related to the sale shall be borne by the Customer.',
    'e) Delivery shall be made at such of the Company’s premises in Greece indicated by the Company in its sole discretion. The Company assumes no liability whatsoever after such point even if it agrees to help the Customer transfer the boat to the forwarding company’s premises (as such is chosen by the Customer) or load the vessel for transport.',
    'f) Property in and title to the Vessel and in general goods supplied by the Company to a Customer shall remain vested in the Company until such time as the contract price (plus any expenses, interest and tax on sales, e.g. VAT, where payable) have been fully paid to the Company without deduction whether by way of legal or equitable set off or otherwise, however risk passes on to Customer at delivery. Pending the passing of property and title the Customer possesses the Product and holds all documents of title to any such vessels, goods, gear and equipment at own risk and as depositary of the Company.',
    'g) The Customer shall take delivery of the Vessel no later than 15 days (for Customers residing in Greece) or 30 days (for Customers residing abroad) from the date the Company has given notice to the Customer that the Vessel is ready for delivery (“Notice of Delivery”). Should the Customer not take delivery of the Vessel within 15 or 30 (as the case may be) days from the date of Notice of Delivery, the Company shall have the right to charge the Customer for docking, maintenance, shrink wrapping, parking (within the Shipyard or at a parking facility outside the Shipyard) and insurance cost on the Vessel. Should the Customer not take delivery of the Vessel within 30 days from the end of the above 15 or 30 (as the case may be) day period, the Company shall have the right to terminate the Sale Agreement.',
  ] },
  { h: '5. PAYMENT', p: [
    'a) The Sale Agreement shall become valid and in effect on receipt by the Company 30% of the Vessel price indicated therein. Such 30% of the Vessel price is non-refundable even when the Company terminated the Sale Agreement or agreed to the cancellation of a Sale Agreement.',
    'b) The remaining 70% of the purchase price shall be paid in a single or more instalments, as indicated in the Sale Agreement and on the dates indicated therein (in the meaning of 341 GrCC).',
    'c) Payment for the Vessel shall be made in full in advance of delivery, in readily available funds and in euro.',
    'd) The Customer shall not, except when so agreed in writing with the Company be entitled to cancel a Sale Agreement for any reason whatsoever.',
    'e) Time for the payment of sums due to the Company shall be of essence and the Company shall be at liberty to terminate a Sale Agreement where money remains unpaid for 30 calendar days and the Company may thereupon dispose of the Vessel appropriated to the Sale Agreement and apply the proceeds of sale to payment of its costs, shortfalls and losses. Such application of costs etc shall be without prejudice to the Company’s rights to recover any damages from the Customer.',
    'f) The Company reserves the right to charge for any late payments interest in accordance with L. 4152/2013.',
  ] },
  { h: '6. VESSEL STATUS', p: [
    'The Vessel is sold as a private vessel and in accordance with the respective CE certificate and the Company does not warrant that it can qualify as a professional vessel under any jurisdiction. Requests for amendments in order for the Vessel to be characterized as professional vessel under any flag may involve additional costs or delay in delivery and the Company shall not be under any obligation to comply with any relevant requests of the Customer and even when she does so, such amendment shall at all times be made based only on the data given by the Customer and the Company will not be liable for the adequacy or appropriateness of instructions received by the Customer.',
  ] },
  { h: '7. WARRANTIES', p: [
    'a) The Company does not warrant:',
    '1. components other than hull and tubes, including but not limited to engines, engine parts, accessories, air conditioning system, transmissions, electronics, batteries, appliances, propellers, generators, and canvas which are exclusively warranted by their manufacturers under the terms of their applicable warranties supplied by their manufactures. The owner shall look exclusively to these manufacturers for any and all such warranty claims. In general, no proprietary article ordered by name type and/or size by an owner shall carry any warranty, save so far as the Company is able to assign the benefit of a manufacturer’s warranty.',
    '2. equipment and accessories not installed by the Company.',
    '3. additional limitations of warranty indicated in the warranty policy included in the Vessel Manual.',
    'b) In any event the sole obligation of the Company shall be to repair the Vessel and replace any defective part. The Company shall not be liable for any damages or losses, in particular without limitation, for damages (whether direct, indirect consequential), crew costs, fuel, diesel oil, management and maintenance costs, financial charges and interest, and expert and/or legal fees resulting from flaws or defects covered by the Warranty.',
  ] },
  { h: '8. VALUE ADDED TAX', p: [
    'Except where otherwise stated the quotation is exclusive of Value Added Tax (“VAT”). The Company will charge VAT and other taxes as may be required by current European legislation.',
  ] },
  { h: '9. GOVERNING LAW', p: [
    'All sale contracts are governed by Greek Law (excluding the United Nations Convention of Contracts for the International Sale of Goods). The Court of Piraeus shall have jurisdiction over any disputes from or in relation to the sale agreements.',
  ] },
  { h: '10. NOTICES', p: [
    'Any notice shall be deemed to have been served if sent by recorded delivery or registered post to the address indicated in the Sale Agreement or such last known address of the Customer made known to the Company by the Customer or to the Registered Office of the Company as the case may be.',
  ] },
  { h: '11. KNOW YOUR CUSTOMER INFORMATION AND AUTHORIZATION', p: [
    'Within 30 days from the date of signing of the Sale Agreement and in any event prior to delivery of the Vessel, the Buyer must provide such recent documents and evidence as may be required by law and/or reasonably required by the Company and/or the Company’s Bank and/or any other authority, to establish and evidence the identity and corporate status, nationality place of domicile, good standing and sources of wealth and/or funds of the Customer and the equivalent information for its shareholders, officers and beneficial owners. Furthermore, no later 20 days prior to delivery, the Customer, must present provide to the company a recent good standing certificate and proper authorisation to sign the bill of sale and the delivery and acceptance protocol.',
  ] },
  { h: '12. PRIVACY', p: [
    'In compliance with European Regulation 2016/679/EU, and related Greek legislation, the Company, shall for the purpose of and in relation to this transaction collect and process personal data of the Customer. The Company’s Privacy Policy can be found at https://technohull.com/privacy-policy/. Please read through our privacy policy before entering into this Agreement.',
  ] },
];

const PRIVACY_ACK = '"I hereby acknowledge that the signing and performance of this agreement requires the collection and process of my personal data and I have read and been informed of the Technohull Privacy Policy uploaded at www.technohull.com, which includes among others information about the processing of my personal data for the purpose of execution of this agreement and the exercise of my rights as a subject of personal data within the meaning of the European Regulation 2016/679/EU."';

// ── renderer ──────────────────────────────────────────────────────────────────────
// Returns false when the browser blocked the print pop-up, so the caller can say so.
function renderAgreement(d: AgreementDoc): boolean {
  // the client reads this, so the date is written out in full ("9 October 2026"), not in a staff preference
  const date10 = formatClientDate((d.orderDate || '').slice(0, 10));
  const chars = CHARACTERISTICS[d.modelKey] ?? [];

  const kv = (k: string, v: string) => `<tr><td class="k">${esc(k)}</td><td>${esc(v || '-')}</td></tr>`;
  const charRows = chars.map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${esc(v)}</td></tr>`).join('');
  const configRows = d.configChoices.map(([k, v]) => `<tr><td class="k">${esc(k)}</td><td>${v ? esc(v) : '-'}</td></tr>`).join('');

  const stdBlocks = d.standardGroups
    .map((g) => `<div class="stdcat"><div class="stdcat-h">${esc(g.title)}</div>${g.items.map((it) => `<div class="stditem">${esc(it)}</div>`).join('')}</div>`)
    .join('');

  const optBody = d.optionalLines.length
    ? d.optionalLines
        .map((l) => `<tr><td>${esc(l.name)}${l.discountEligible === false ? ' <span class="tag">no discount</span>' : ''}</td><td class="num">${l.qty}</td><td class="num">${eur(l.unit)}</td><td class="num">${eur(l.amount)}</td></tr>`)
        .join('')
    : `<tr><td colspan="4"><i>No optional equipment selected.</i></td></tr>`;

  const down = Math.round(d.pricing.total * 0.3 * 100) / 100;
  const final = Math.round((d.pricing.total - down) * 100) / 100;

  const footnotes = FOOTNOTES.map(([m, t]) => `<p class="fn"><span class="fn-m">${esc(m)}</span> ${esc(t)}</p>`).join('');
  const banks = BANKS.map((b) => `<div class="card"><div class="card-h">${esc(b.name)}</div><div>IBAN: ${esc(b.iban)}</div><div>BIC: ${esc(b.bic)}</div><div>Beneficiary: TECHNOHULL S.A.</div></div>`).join('');
  const terms = TERMS.map((s) => `<div class="term"><div class="term-h">${esc(s.h)}</div>${s.p.map((p) => `<p>${esc(p)}</p>`).join('')}</div>`).join('');
  const disclaimer = DISCLAIMER.map((t) => `<li>${esc(t)}</li>`).join('');

  const headerHtml = `
    <div class="head">
      <img class="logo" src="${LOGO_DATA_URI}" alt="TECHNOHULL" />
      <div class="head-r">
        <div class="brand">TECHNOHULL S.A.</div>
        <div class="muted">Iroos Matsi &amp; Archeou Theatrou, 17456 Alimos, Attica, Greece</div>
        <div class="muted">Shipyard: 1st km Lavriou-Souniou Ave., 19500 Lavrio, Greece</div>
        <div class="muted">Telephone: +30 22920 26719 &nbsp;&middot;&nbsp; VAT No: EL 999891900</div>
      </div>
    </div>`;
  const signBlock = `
    <div class="sign">
      <div class="sign-buyer">
        <div class="sign-k">Date:</div>
        <div class="sign-k">Name:</div>
        <div class="sign-line"></div>
        <div class="sign-cap">Buyer - signature</div>
      </div>
      <div class="sign-co">
        <div class="sign-k">Stamp and signature</div>
        <img class="seal" src="${SEAL_SIGNATURE_DATA_URI}" alt="TECHNOHULL S.A. stamp and signature" />
        <div class="sign-cap">For and on behalf of TECHNOHULL S.A.</div>
      </div>
    </div>`;

  const body = `
    <div class="doc">
      <section class="page">
        ${headerHtml}
        <div class="pf-parties">
          <div class="pf-party">
            <div class="pf-lbl">Buyer</div>
            <div class="pf-val">${esc(d.buyer.name || '-')}</div>
            ${d.buyer.registeredAddress ? `<div class="muted">${esc(d.buyer.registeredAddress)}</div>` : ''}
            ${d.buyer.vatTaxCode ? `<div class="muted">VAT / Tax Code: ${esc(d.buyer.vatTaxCode)}</div>` : ''}
          </div>
          <div class="pf-party">
            <div class="pf-lbl">Builder</div>
            <div class="pf-val">TECHNOHULL S.A.</div>
            <div class="muted">Iroos Matsi &amp; Archeou Theatrou, 17456 Alimos, Attica, Greece</div>
            <div class="muted">Shipyard: 1st km Lavriou-Souniou Ave., 19500 Lavrio, Greece</div>
            <div class="muted">VAT No: EL 999891900</div>
          </div>
        </div>
        <h1>PROFORMA INVOICE</h1>
        <div class="meta"><span>No: <b>${esc(d.proformaNumber || '-')}</b></span><span>Date: <b>${esc(date10 || '-')}</b></span></div>
        <p class="lead">With reference to the Boat Sale Agreement for the purchase of a Technohull boat.</p>
        <table class="totals"><tr class="grand"><td>Down payment - Technohull <b>${esc(d.vessel.modelName || 'ALPHA 40')}</b> (T# ${esc(d.hull || '-')})</td><td class="num">${eur(down)}</td></tr></table>
        <div class="sub-h">Terms &amp; conditions of transaction</div>
        <ol class="numlist">
          <li>Boat is delivered ex-works with all agreed equipment fitted.</li>
          <li>All banking commissions and charges outside Greece are on the Buyer's account.</li>
        </ol>
        <div class="sub-h">References of the Builder's bank</div>
        <div class="cols">${banks}</div>
        ${signBlock}
      </section>

      <section class="page pagebreak">
      ${headerHtml}

      <h1>BOAT SALE AGREEMENT</h1>
      <div class="meta"><span>Proforma Invoice No: <b>${esc(d.proformaNumber || '-')}</b></span><span>Date: <b>${esc(date10 || '-')}</b></span></div>

      <div class="sec-h">Buyer details</div>
      <table class="kv">
        ${kv('Name / Surname', d.buyer.name)}${kv('Registered Address', d.buyer.registeredAddress)}
        ${kv('VAT & Tax Code', d.buyer.vatTaxCode)}${kv('Profession', d.buyer.profession)}${kv('Email', d.buyer.email)}
      </table>

      <div class="sec-h">Delivery details</div>
      <table class="kv">
        ${kv('Delivery *', d.delivery.method)}${kv('Final Destination', d.delivery.destination)}${kv('Estimated Delivery Period **', d.delivery.estimatedDeliveryPeriod)}
      </table>

      <div class="sec-h">Vessel details</div>
      <div class="vessel-row">
        <table class="kv">
          ${kv('Model', d.vessel.modelName)}${kv('Model Year', d.vessel.modelYear)}${kv('Flag', d.vessel.flag)}${kv('Vessel Name', d.vessel.vesselName)}
        </table>
        <img class="wordmark" src="${WORDMARK_DATA_URI}" alt="${esc(d.vessel.modelName)}" />
      </div>

      ${charRows ? `<div class="sec-h">General characteristics</div><table class="kv">${charRows}</table>` : ''}

      <div class="sec-h">Vessel configuration</div>
      <table class="kv">${configRows}</table>

      <table class="totals headline">
        <tr class="net"><td>NET PRICE EX WORKS</td><td class="num">${eur(d.pricing.net)}</td></tr>
        <tr class="grand"><td>TOTAL NET VESSEL PRICE EX WORKS INCLUDING VAT (IF DUE)</td><td class="num">${eur(d.pricing.total)}</td></tr>
      </table>

      <div class="sec-h pagebreak">Standard equipment</div>
      <div class="stdgrid">${stdBlocks}</div>

      <div class="sec-h">Pricing - engines &amp; list of optional equipment</div>
      <table class="grid"><thead><tr><th>Description</th><th class="num">Qty</th><th class="num">Unit price</th><th class="num">Amount</th></tr></thead>
        <tbody>${optBody}</tbody></table>

      <table class="totals">
        <tr><td>TOTAL VESSEL PRICE (excl. discount)</td><td class="num">${eur(d.pricing.optional)}</td></tr>
        <tr><td>DISCOUNT (${DEALER_DISCOUNT_PCT}% of eligible)</td><td class="num">− ${eur(d.pricing.discount)}</td></tr>
        <tr class="net"><td>TOTAL VESSEL NET PRICE EX WORKS</td><td class="num">${eur(d.pricing.net)}</td></tr>
        <tr><td>VAT ${VAT_PCT}% (IF DUE)</td><td class="num">${eur(d.pricing.vat)}</td></tr>
        <tr class="grand"><td>TOTAL VESSEL NET PRICE EX WORKS INCLUDING VAT (IF DUE)</td><td class="num">${eur(d.pricing.total)}</td></tr>
      </table>

      <div class="disc">
        <b>Disclaimer:</b>
        <ul>${disclaimer}</ul>
      </div>

      <div class="sec-h">Payment schedule ***</div>
      <table class="totals">
        <tr><td>Down payment (30%)</td><td class="num">${eur(down)}</td></tr>
        <tr><td>Final payment (70%)</td><td class="num">${eur(final)}</td></tr>
      </table>
      <p class="fn">Final payment due when notified that the vessel is ready for delivery and in any case prior to the vessel’s transfer of ownership and shipment.</p>
      ${footnotes}

      <div class="sec-h">Bank account details</div>
      <div class="cols">${banks}</div>
      <p class="fn">${esc(FUNDS_NOTE)}</p>

      <div class="sec-h pagebreak">Appendix to the sale agreement - Terms of Sale</div>
      <p class="intro">${esc(TERMS_INTRO)}</p>
      ${terms}

      <p class="ack">${esc(PRIVACY_ACK)}</p>
      <p class="consent">[&nbsp;&nbsp;&nbsp;] I hereby consent to receiving by the Company advertising and marketing material.</p>

      ${signBlock}
      </section>
    </div>`;

  const w = window.open('', '_blank', 'width=1000,height=1200');
  if (!w) return false;
  w.document.write(`<!doctype html><html><head><meta charset="utf-8"><title>Boat Sale Agreement - ${esc(d.title)}</title>
  <link rel="preconnect" href="https://fonts.googleapis.com"><link rel="preconnect" href="https://fonts.gstatic.com" crossorigin>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;500;600;700;800&display=swap');
    *{box-sizing:border-box}
    body{font-family:'Montserrat','Helvetica Neue',Arial,sans-serif;color:#1a1a1a;background:#fff;margin:0;font-size:11px;line-height:1.45;-webkit-font-smoothing:antialiased}
    .doc{max-width:820px;margin:0 auto;padding:24px}
    .head{display:flex;justify-content:space-between;align-items:flex-start;gap:16px;border-bottom:2px solid #111;padding-bottom:12px;margin-bottom:4px}
    .logo{height:38px;width:auto}
    .head-r{text-align:right}
    .brand{font-weight:800;font-size:15px;letter-spacing:.02em}
    .muted{color:#6b6b6b;font-size:10px;line-height:1.5}
    h1{font-weight:800;font-size:22px;letter-spacing:.06em;text-align:center;margin:20px 0 4px;text-transform:uppercase}
    .meta{display:flex;justify-content:center;gap:28px;font-size:11px;color:#333;margin-bottom:12px}
    /* light-gray section band, matching the source workbook (#D8D8D8, bold, black) */
    .sec-h{font-weight:700;font-size:13px;text-transform:uppercase;letter-spacing:.04em;color:#111;background:#d8d8d8;padding:6px 10px;margin:18px 0 8px}
    .sub-h{font-weight:700;font-size:11.5px;text-transform:uppercase;letter-spacing:.03em;color:#111;margin:14px 0 5px;border-bottom:1px solid #d8d8d8;padding-bottom:3px}
    .pagebreak{break-before:page}
    .lead{font-size:11.5px;color:#222;margin:8px 0 10px}
    .pf-parties{display:flex;gap:24px;margin:16px 0 4px}
    .pf-party{flex:1}
    .pf-lbl{font-weight:700;font-size:10px;text-transform:uppercase;letter-spacing:.06em;color:#6b6b6b;margin-bottom:2px}
    .pf-val{font-weight:700;font-size:13px}
    table{border-collapse:collapse;width:100%}
    table.kv td{padding:3px 8px;font-size:11px;vertical-align:top;border-bottom:1px solid #ececec}
    table.kv td.k{color:#555;width:42%;font-weight:600}
    .vessel-row{display:flex;gap:16px;align-items:center}
    .vessel-row .kv{flex:1}
    .wordmark{height:56px;width:auto}
    table.grid{font-size:11px;margin-top:2px} table.grid th,table.grid td{border:1px solid #d0d0d0;padding:5px 8px;text-align:left}
    table.grid th{background:#111;color:#fff;font-size:10px;text-transform:uppercase;letter-spacing:.03em}
    .num{text-align:right;font-variant-numeric:tabular-nums;white-space:nowrap}
    .tag{font-size:8px;color:#8a5a00;border:1px solid #d8b46a;border-radius:3px;padding:0 3px;vertical-align:middle}
    table.totals{margin-top:10px;font-size:12px} table.totals td{padding:4px 10px}
    table.totals tr td:last-child{width:34%}
    table.totals.headline{margin-top:12px}
    table.totals tr.net td{border-top:1px solid #bbb;font-weight:700}
    table.totals tr.grand td{border-top:2px solid #111;border-bottom:2px solid #111;font-weight:800;font-size:13px;background:#f4f4f4}
    .cols{display:flex;gap:14px;margin-top:4px}
    .card{flex:1;border:1px solid #d8d8d8;border-radius:4px;padding:8px 11px;font-size:11px}
    .card-h{font-weight:700;font-size:11px;text-transform:uppercase;letter-spacing:.03em;margin-bottom:4px}
    .stdgrid{columns:2;column-gap:20px;font-size:10px}
    .stdcat{break-inside:avoid;margin-bottom:8px} .stdcat-h{font-weight:700;font-size:9.5px;text-transform:uppercase;color:#111;border-bottom:1px solid #e4e4e4;margin-bottom:2px}
    .stditem{color:#333;padding-left:8px;line-height:1.5}
    .disc{font-size:10px;color:#333;margin-top:10px} .disc ul{margin:4px 0;padding-left:16px}
    .numlist{font-size:11px;color:#222;margin:4px 0;padding-left:18px} .numlist li{margin:2px 0}
    .intro{font-size:10.5px;color:#333;margin:4px 0 8px}
    .term{margin-bottom:9px;break-inside:avoid} .term-h{font-weight:700;font-size:10.5px;color:#111;letter-spacing:.02em}
    .term p{font-size:9.5px;color:#333;margin:2px 0;text-align:justify}
    .fn{font-size:9px;color:#555;margin:4px 0} .fn-m{font-weight:700;color:#111}
    .ack{font-size:9.5px;color:#333;font-style:italic;margin-top:12px}
    .consent{font-size:10px;color:#333;margin:6px 0}
    .sign{display:flex;justify-content:space-between;gap:48px;margin-top:26px;break-inside:avoid}
    .sign>div{flex:1}
    .sign-k{font-size:10.5px;color:#333;margin-bottom:12px}
    .sign-line{border-top:1px solid #333;margin:24px 0 4px}
    .sign-cap{font-size:9.5px;color:#6b6b6b}
    .sign-co{text-align:center}
    .seal{max-height:120px;max-width:100%;width:auto;margin:2px auto 4px;display:block}
    @page{size:A4;margin:13mm}
  </style></head><body>${body}<script>
    (function(){var go=function(){setTimeout(function(){window.print();},200);};
     if(document.fonts&&document.fonts.ready){document.fonts.ready.then(go);setTimeout(go,1500);}else{window.onload=go;}})();
  </script></body></html>`);
  w.document.close();
  return true;
}

// ── entry point 1: live Configurator (catalogue + selection map) ─────────────────
export interface AgreementLine { selected?: boolean; quantity?: number; customerPrice?: number | null }
export interface AgreementInput {
  orderNumber: string;
  proformaNumber: string;
  orderDate: string;
  buyer: { name: string; registeredAddress: string; vatTaxCode: string; profession: string; email: string; telephone: string };
  delivery: { method: string; destination: string; estimatedDeliveryPeriod: string };
  vessel: { modelName: string; modelYear: string; flag: string; vesselName: string; sapNumber: string; modelCode: string };
  categories: CatalogueCategory[];
  lines: Record<number, AgreementLine>;
  pricing: { optional: number; discount: number; net: number; vat: number; total: number };
}

export function exportSalesAgreement(d: AgreementInput): boolean {
  const isSelected = (id: number) => !!d.lines[id]?.selected;

  const configChoices = CONFIG_CATEGORIES.map((name): [string, string] => {
    const cat = d.categories.find((c) => c.name === name && c.kind === 'optional');
    const chosen = cat?.items.filter((it) => isSelected(it.id)).map((it) => it.name) ?? [];
    return [name, chosen.join(', ')];
  });

  const standardGroups = d.categories
    .filter((c) => c.kind === 'standard')
    .map((c) => ({ title: c.name, items: c.items.map((it) => it.name) }));

  const optionalLines: OptionalLine[] = [];
  for (const c of d.categories.filter((c) => c.kind === 'optional')) {
    for (const it of c.items) {
      if (!isSelected(it.id)) continue;
      const unit = d.lines[it.id]?.customerPrice ?? it.customer_price ?? 0;
      if (!unit) continue;
      const qty = d.lines[it.id]?.quantity ?? 1;
      optionalLines.push({ name: it.name, qty, unit, amount: unit * qty, discountEligible: c.discountEligible !== false });
    }
  }

  return renderAgreement({
    title: d.vessel.vesselName || d.orderNumber,
    proformaNumber: d.proformaNumber || d.orderNumber,
    orderDate: d.orderDate,
    buyer: d.buyer,
    delivery: d.delivery,
    vessel: { modelName: d.vessel.modelName, modelYear: d.vessel.modelYear, flag: d.vessel.flag, vesselName: d.vessel.vesselName },
    hull: d.vessel.sapNumber || '',
    modelKey: modelKeyOf(d.vessel.modelCode || d.vessel.modelName),
    configChoices,
    standardGroups,
    optionalLines,
    pricing: d.pricing,
  });
}

// ── entry point 2: committed Sales Order snapshot ────────────────────────────────
export function exportAgreementFromSnapshot(a: { orderNumber: string; snapshot: OrderSnapshot; categoryOrder?: string[] }): boolean {
  const s = a.snapshot;
  const eq = s.equipment ?? [];
  const catUp = (e: (typeof eq)[number]) => (e.category ?? '').toUpperCase();

  const configChoices = CONFIG_CATEGORIES.map((name): [string, string] => {
    const chosen = eq
      .filter((e) => e.selected && catUp(e) === name)
      .map((e) => [e.name, e.color].filter(Boolean).join(' · '))
      .filter(Boolean);
    return [name, chosen.join(', ')];
  });

  const stdByCat = new Map<string, string[]>();
  for (const e of eq) {
    if (!e.isStandard || e.removedFromStandard) continue;
    const cat = e.category || 'STANDARD';
    if (!stdByCat.has(cat)) stdByCat.set(cat, []);
    if (e.name) stdByCat.get(cat)!.push(e.name);
  }
  const order = a.categoryOrder && a.categoryOrder.length ? a.categoryOrder : [...stdByCat.keys()];
  const seen = new Set<string>();
  const standardGroups = [...order, ...stdByCat.keys()]
    .filter((c) => stdByCat.has(c) && !seen.has(c) && (seen.add(c), true))
    .map((c) => ({ title: c, items: stdByCat.get(c)! }));

  const optionalLines: OptionalLine[] = eq
    .filter((e) => e.selected && !e.isStandard && (e.customerPrice ?? 0) > 0)
    .map((e) => {
      const qty = e.quantity || 1;
      const unit = e.customerPrice || 0;
      return { name: e.name || `#${e.equipmentId}`, qty, unit, amount: unit * qty, discountEligible: e.discountEligible !== false };
    });

  const p = s.pricing;
  return renderAgreement({
    title: s.vessel?.vesselName || a.orderNumber,
    proformaNumber: s.proformaNumber || a.orderNumber || '',
    orderDate: s.orderDate || '',
    buyer: {
      name: s.buyer?.name || '', registeredAddress: s.buyer?.registeredAddress || '', vatTaxCode: s.buyer?.vatTaxCode || '',
      profession: s.buyer?.profession || '', email: s.buyer?.email || '', telephone: s.buyer?.telephone || '',
    },
    delivery: { method: s.delivery?.method || '', destination: s.delivery?.destination || '', estimatedDeliveryPeriod: s.delivery?.estimatedDeliveryPeriod || '' },
    vessel: { modelName: s.modelName || '', modelYear: s.vessel?.modelYear ? String(s.vessel.modelYear) : '', flag: s.vessel?.flag || '', vesselName: s.vessel?.vesselName || '' },
    hull: s.vessel?.sapNumber || s.vessel?.hullNumber || '',
    modelKey: modelKeyOf(s.modelName),
    configChoices,
    standardGroups,
    optionalLines,
    pricing: { optional: p?.subtotalOptional ?? 0, discount: p?.discount ?? 0, net: p?.net ?? 0, vat: p?.vat ?? 0, total: p?.total ?? 0 },
  });
}
